import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Add as AddIcon } from '@mui/icons-material';
import { Button } from '@mui/material';
import { isEmpty, isEqual, isEqualWith, isFunction, orderBy, uniq } from 'lodash';
import { Allocation, CapTableDetailsModel, MeasurementDate, SecurityModel } from 'api';
import { captableAction, firmsAction } from 'common/actions';
import { ERROR_403 } from 'common/config/api';
import { FUND_OWNERSHIP_REFERENCE_TITLE, FUND_OWNERSHIP_REFERENCE_TYPE } from 'common/constants/documents';
import * as FOC from 'common/constants/fund-ownership/fundOwnership';
import { FUND_OWNERSHIP_LOCKED_MESSAGE } from 'common/constants/fund-ownership/fundOwnership';
import { FUND_OWNERSHIP_PAGE_KEY } from 'common/constants/notes';
import { UNISSUED_OPTIONS } from 'common/constants/securityTypes';
import { FIRM_PERMISSION_NAME, FUND_PERMISSION_NAME } from 'common/constants/sidebar';
import { useFormat } from 'common/hooks';
import { useStore } from 'common/store';
import { CapTableVersion } from 'common/types/company';
import { FundOwnershipDetail, FundOwnership as FundOwnershipType } from 'common/types/fundOwnership';
import { FieldAttribute, UseStoreValues } from 'common/types/store';
import { Alert, AllocationFinalMessage, EmptyTableMessage, MessageBox } from 'components';
import { FinalAllocation } from 'components/AllocationFinalMessage/AllocationFinalMessage.props';
import { ExtendedFabButton } from 'components/Buttons';
import { Z_INDEX_FOR_ADD_BUTTONS } from 'components/Buttons/constants';
import { CellAnnotationProvider, useHydrateCaptableCellAnnotations } from 'components/CellAnnotationValueViewer';
import { OptionType } from 'components/CustomSelect/types';
import { ConfirmationDialog } from 'components/Dialogs';
import {
  APP_DRAGGABLE_DRAWER_HEIGHT_VAR,
  MIN_DRAWER_HEIGHT,
} from 'components/Drawer/draggable-drawer/DraggableDrawer.constants';
import WidgetDrawer from 'components/Drawer/widget-drawer/WidgetDrawer';
import { DEFAULT_CURRENCY, DEFAULT_CURRENCY_SYMBOL } from 'components/FeaturedSpreadsheet/constants';
import { FloatingButtonGroup } from 'components/FloatingButtonGroup';
import { GridSkeleton } from 'components/Grid';
import ScalarSpreadsheet from 'components/ScalarSpreadsheet';
import useWorkbook from 'components/ScalarSpreadsheet/utilities/useWorkbook';
import WorkbookContext from 'components/ScalarSpreadsheet/utilities/WorkbookContext';
import DialogContext from 'context/DialogContext';
import { FundOwnershipContextType } from 'context/FundOwnershipContext';
import { useLayoutContext } from 'context/LayoutContext';
import { useExportSignal } from 'features/pdfExport';
import { EvaluatedCell, ScalarSpreadsheetConfigDataInterface } from 'models/interfaces/spreadsheet-types/core-types';
import { EmptyCapTableMessage, ProformaCaptableDialog } from 'pages/CapTable/cap-table/components';
import { PdfExportTargets } from 'pages/Valuations/util/constants';
import { RelatedEntity } from 'pages/ValuationsAllocation/types';
import { useDocuments, useKeyEventForFabButton, useReadFieldAttributes } from 'services/hooks';
import { useUpdateCaptableFundOwnership } from 'services/hooks/captable';
import { useNotes } from 'services/hooks/notes';
import useSpreadsheetNotes from 'services/hooks/spreadsheetNotes/useSpreadsheetNotes';
import { FUND_OWNERSHIP_PAGE } from 'services/hooks/useReadFieldAttributes/constants';
import { dividendsPerShareAtDate, getNumberValue, gridShortDate, toString } from 'utilities';
import { ConfirmationSaveFinal, FundsChipContainer, SecuritiesChipContainer } from './components';
import { fundOwnershipDetailsTraverser, initialTableData, tableTerms } from './constants';
import { rowConfig } from './data';
import { classes, StyledFundOwnershipContextProvider } from './FundOwnership.styles';
import { FundOwnershipTableData } from './models/fund-ownership.models';
import { FundOwnershipCapTable, FundOwnershipConfigurationRow, ReverseParsedFundOwnershipDetail } from './types';
import useFundOwnership from './useFundOwnership';
import useFundOwnershipActions from './useFundOwnershipActions';
import { getReferences } from './utilities';
import { WidgetDrawerApplicationContext } from '../../../components/Drawer/widget-drawer/WidgetDrawer.props';
import {
  BASIS_3_VALUE,
  ISSUE_PRICE_VALUE,
} from '../cap-table/components/liquidation-preference-ledger/liq-pref-ledger-table-config/constants';
import { readLiquidationPreferenceConfig } from '../cap-table/utilities';
import { LedgerConfig } from '../cap-table/utilities/readLiquidationPreferenceConfig.props';

interface FundOwnershipProps {
  selectedVersions?: number;
  setSelectedTab?: (tab: number) => void;
  saveAsNewProforma: (data: any) => void;
  capTableVersionsByCompany: Record<string, CapTableVersion[]>;
  selectedMeasurementDate?: MeasurementDate;
  reloadAllocationBreadcrumbInfo: (allocationId: number, companyExchangeRate: any) => void;
  isActiveTab?: boolean;
}

// any because the
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const customizerSkipFunction = (objValue: any, othValue: any): boolean | undefined => {
  if (isFunction(objValue) && isFunction(othValue)) {
    return true;
  }
};

const FundOwnership = ({
  saveAsNewProforma,
  selectedMeasurementDate,
  reloadAllocationBreadcrumbInfo,
  capTableVersionsByCompany,
  isActiveTab = true,
}: FundOwnershipProps) => {
  const { updatePageActions, viewOnlyUser, companyExchangeRate, isExportMode } = useLayoutContext();
  const { showDialog } = useContext(DialogContext);
  const [
    {
      companyInfo,
      captableInfo,
      allocationInfo,
      isShowLoadingProgress,
      fundList,
      otherPermissions,
      fundInfo,
      user,
      firmId,
      selectedUserPermissions,
    },
    dispatch,
  ] = useStore() as unknown as UseStoreValues;

  const initialFormValues = useMemo(
    () => ({
      isValid: true,
      name: `${captableInfo?.name} Proforma`,
      values: {} as unknown as Record<string, unknown>,
      errors: {},
    }),
    [captableInfo]
  );

  const [openProformaDialog, setOpenProformaDialog] = useState(false);
  const [formState, setFormState] = useState(initialFormValues);
  const [tableData, setTableData] = useState<FundOwnershipTableData>(initialTableData);
  const [columns, setColumns] = useState<ReverseParsedFundOwnershipDetail[] | undefined>();
  const [filteredByFundsColumns, setFilteredByFundsColumns] = useState<ReverseParsedFundOwnershipDetail[]>([]);
  const [visibleColumns, setVisibleColumns] = useState<ReverseParsedFundOwnershipDetail[]>([]);
  const [customConfig, setCustomConfig] = useState<FundOwnershipConfigurationRow[]>([]);
  const [isDisabled, setIsDisabled] = useState<boolean>(false);
  const [isAllocationFinal, setIsAllocationFinal] = useState<boolean>(false);
  const [resetColumns, setResetColumns] = useState(false);
  const [referencedValues, setReferencedValues] = useState({});
  const [finalAllocations, setFinalAllocations] = useState<FinalAllocation[]>([]);
  const [captableId, setCaptableId] = useState<number>();
  const [isLoaded, setIsLoaded] = useState<boolean>(); // When all the requests are finished
  const { filesToSave, setFilesToSave, addReferenceForExistingDocuments } = useDocuments();
  const { notes, setNotes, notesHasChanged, saveNotes, onAddNote, onUpdateNotes, onDeleteNote } = useNotes();
  const {
    spreadsheetNotes,
    setSpreadsheetNotes,
    saveSpreadsheetNotes,
    hasChanged: spreadsheetNotesHasChanged,
    handleOnDeleteNote: handleOnDeleteSpreadsheetNote,
  } = useSpreadsheetNotes();
  const [dividends, setDividends] = useState<Record<string, number>>({});
  const fieldAttributes: Map<string, FieldAttribute> = useReadFieldAttributes(FUND_OWNERSHIP_PAGE);
  const [sheets, setSheets] = useState<ScalarSpreadsheetConfigDataInterface[]>([]);
  const [isDisplayingRowNumbers, setIsDisplayingRowNumbers] = useState(false);
  const [fundEntityListValues, setFundEntityListValues] = useState({});
  const [securityListValues, setSecurityListValues] = useState({});
  const gridDataRef = useRef<FundOwnershipTableData | null>(null);

  // custom hooks
  const { updateFundOwnershipData } = useUpdateCaptableFundOwnership();

  const [format, formatDispatch] = useFormat({
    page: 'captable',
    units: `${DEFAULT_CURRENCY} ${DEFAULT_CURRENCY_SYMBOL}`,
  });

  const handleSecurityListValues = useCallback((securities: OptionType[], columnLegend: string) => {
    setSecurityListValues(prev => ({ ...prev, [columnLegend]: securities }));
  }, []);

  const handleFundEntityListValues = useCallback((funds: OptionType[], columnLegend: string) => {
    setFundEntityListValues(prev => ({ ...prev, [columnLegend]: funds }));
  }, []);

  const { spreadsheet } = useFundOwnership({
    visibleColumns,
    setVisibleColumns,
    customConfig,
    tableData,
    tableTerms,
    columns,
    isDisabled,
    fieldAttributes,
    captableInfo: captableInfo as CapTableDetailsModel,
    capTableVersionsByCompany,
  });

  const addCustomSecurityOwnership = () => addColumn(true);

  useEffect(() => {
    if (spreadsheet) {
      setSheets([spreadsheet]);
    }
  }, [spreadsheet]);

  const { onChange: onWorkbookChange, setCells, workbook, areCellsValid, workbookContextValue } = useWorkbook(sheets);

  const onChange = useCallback(
    (cell: EvaluatedCell, expr, customFn) => {
      // this is the output of fund ownership reverse parser
      const newestData = onWorkbookChange(cell, expr, customFn)[0];
      gridDataRef.current = newestData;
    },
    [onWorkbookChange]
  );

  const spreadsheetTableData = useMemo(() => spreadsheet?.tableData ?? {}, [spreadsheet?.tableData]);

  const { deleteColumn, addColumn, cloneColumn } = useFundOwnershipActions(spreadsheet);

  const hasOwnershipsOnSecuritiesWithDividends = useMemo(() => {
    const capTableSecurities = captableInfo?.securities;
    const updatedColumns = spreadsheet?.tableData.columns;
    if (updatedColumns && capTableSecurities) {
      // Get only the info of the selected securities
      const selectedSecurities = capTableSecurities.filter(security =>
        updatedColumns.some(
          (column: ReverseParsedFundOwnershipDetail) => Number(column.security) === Number(security.id)
        )
      );
      // Check if any of the selected security has dividends
      return selectedSecurities.filter(security => security.has_dividends).length > 0;
    }
    return false;
  }, [spreadsheet, captableInfo]);

  // Check if the user has explicit permission for the firm
  const userFirmPermissions = useMemo(() => {
    if (selectedUserPermissions && firmId) {
      return selectedUserPermissions?.filter(
        p => p?.feature_object?.object_id === firmId && p?.feature_object?.object_type === FIRM_PERMISSION_NAME
      );
    }

    return [];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, firmId]);

  const isSingleFundUser = useMemo(() => {
    if (otherPermissions) {
      return (
        otherPermissions?.filter(({ feature_object }) => feature_object?.object_type === FUND_PERMISSION_NAME)
          .length === 1 && isEmpty(userFirmPermissions)
      );
    }

    return false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [otherPermissions]);

  const getInitialData = () => {
    const foData = (captableInfo?.fund_ownership as FundOwnershipType) ?? ({} as unknown as FundOwnershipType);

    // Drop synthetic CFL-only FODs (`historical_record: true`). They have no
    // database row, so rendering them as editable columns would confuse
    // analysts, any edit would be silently dropped by the PUT filter in
    // `updateFundOwnershipData`. Surface them only on read-mostly pages
    // (Valuations, Schedule of Investments) that consume the same response.
    const fods = (foData?.fund_ownership_detail ?? [])
      .filter((fod: FundOwnershipDetail) => !(fod as { historical_record?: boolean }).historical_record)
      .map((fod: FundOwnershipDetail) => ({
        ...fod,
        title: fod.custom_security_name ?? fod.security_name,
      }));

    setTableData({
      capTableVersionsByCompany,
      fundOwnership: { ...foData, fund_ownership_detail: fods, deleted_fund_ownership_details: [] },
      capTable: captableInfo as FundOwnershipCapTable,
      allocation: allocationInfo as Allocation,
      calculations: {},
    } as any);

    // Add fund ownership columns
    if (fods) {
      const details: FundOwnershipDetail[] = orderBy(fods, 'id', 'desc');
      const detailsToSet =
        isSingleFundUser && !isEmpty(fundInfo) ? details.filter(({ fund }) => fund === fundInfo.id) : details;

      const mappedDetails = detailsToSet.map(detail => {
        const mappedModel = {
          ...detail,
          fund_entity: detail.fund ?? detail.related_entity,
        } as Partial<ReverseParsedFundOwnershipDetail>;

        return mappedModel;
      }) as ReverseParsedFundOwnershipDetail[];

      setColumns(mappedDetails);
    } else {
      // If there is no fund_ownership_detail add an empty array to hide the skeleton
      setColumns([]);
    }
  };

  useEffect(() => {
    if (isEmpty(columns) || !selectedMeasurementDate) {
      return;
    }

    const securities = tableData.capTable?.securities;
    const dividendsById: Record<string, number> = {};

    columns?.forEach(column => {
      const columnData = tableData?.fundOwnership?.fund_ownership_detail?.find(element => element.id === column?.id);

      if (!columnData) {
        return;
      }

      const security = securities?.find(element => element.id === column.security);

      if (!security) {
        return;
      }

      let yearfracBasis = BASIS_3_VALUE;

      if (security.liquidation_preference_ledger_config) {
        const { yearfracBasis: ledgerYearfracBasis } = readLiquidationPreferenceConfig(
          security.liquidation_preference_ledger_config as LedgerConfig,
          selectedMeasurementDate?.date,
          security.investment_date!,
          security.issue_price!,
          security.shares_outstanding!
        );
        yearfracBasis = ledgerYearfracBasis;
      }

      const hasCumulativeDividends = !!security?.has_dividends;
      const id = column?.id?.toString() as string;
      const dividendsAux = dividendsPerShareAtDate({
        hasCumulativeDividends,
        periodStartDate: security.investment_date!,
        hasCompounding: security.has_compounding!,
        periodEndDate: selectedMeasurementDate.date,
        periodIssuePrice: Number(security.issue_price),
        compoundingPeriods: security.compounding_period,
        dividendRate: Number(security.dividend_rate),
        yearfracBasis,
      });

      dividendsById[id] = dividendsAux;
    });

    setDividends(dividendsById);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columns, selectedMeasurementDate]);

  useEffect(() => {
    const calculations: Record<
      string,
      { cumulativeDividends: number; totalSharesOutstanding: number; multipleOfFactor: number }
    > = {};
    const securities = tableData.capTable?.securities;
    if (isEmpty(dividends) || isEmpty(columns)) {
      return;
    }
    columns?.forEach(column => {
      const columnData = tableData?.fundOwnership?.fund_ownership_detail?.find(element => element.id === column.id);

      if (!columnData) {
        return;
      }
      const id = column.id!;
      const security = securities?.find(element => element.id === column.security);

      if (!security) {
        return;
      }
      const outstandingShares = columnData?.shares;
      if (!dividends[id] || !outstandingShares) {
        return;
      }
      const cumulativeDividends = dividends[id] * Number(outstandingShares);
      const totalSharesOutstanding = Math.round(
        (Number(security?.dividend_payment_type) * Number(cumulativeDividends)) / Number(security?.issue_price) +
          Number(outstandingShares)
      );
      const { multipleOf, liquidationPreference } = security.liquidation_preference_ledger_config
        ? readLiquidationPreferenceConfig(
            security.liquidation_preference_ledger_config as LedgerConfig,
            measurementDate!,
            security.investment_date!,
            security.issue_price!,
            security.shares_outstanding!
        ) : { multipleOf: ISSUE_PRICE_VALUE, liquidationPreference: security.liquidation_preference };
      const multipleOfFactor = multipleOf === ISSUE_PRICE_VALUE ? 1 : Number(liquidationPreference);

      calculations[id] = {
        cumulativeDividends,
        totalSharesOutstanding,
        multipleOfFactor,
      };
    });

    setTableData(tableData => ({ ...tableData, calculations }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dividends, columns]);

  useEffect(() => {
    if (captableInfo?.id) {
      setCaptableId(captableInfo.id);

      const verifyAllocationStatus = async () => {
        try {
          const allocations = (await captableAction.verifyIfHasFinalAllocation(captableInfo.id!)) as FinalAllocation[];

          setIsAllocationFinal(!isEmpty(allocations));
          setIsDisabled(!isEmpty(allocations) || viewOnlyUser);
          setFinalAllocations(allocations);
          setIsLoaded(true);
          if (isExportMode) {
            setIsAllocationFinal(true);
            setIsDisabled(true);
          }
          // any as our error is not typed
        } catch (error: any) {
          throw new Error(error);
        }
      };

      verifyAllocationStatus();
    }

    return () => {
      setCaptableId(undefined);
      setIsLoaded(undefined);
    };
  }, [captableInfo, allocationInfo, viewOnlyUser, isExportMode]);

  useEffect(() => {
    if (columns) {
      const newVisibleColumns = columns.filter(({ is_deleted }) => !is_deleted);
      if (!isEqual(visibleColumns, newVisibleColumns)) {
        setVisibleColumns(newVisibleColumns);
      }
      const auxConfig = rowConfig(hasOwnershipsOnSecuritiesWithDividends, isDisabled);

      if (!isEqualWith(customConfig, auxConfig, customizerSkipFunction)) {
        // only if the rowConfig change
        setCustomConfig(auxConfig);
      }
      if (!isEmpty(captableInfo)) {
        const tmpReferencedValues = getReferences({
          columns: newVisibleColumns,
          funds: fundList ?? [],
          securities: captableInfo.securities ?? [],
        });

        if (!isEqual(referencedValues, tmpReferencedValues)) {
          setReferencedValues(tmpReferencedValues);
        }
      }
      if (!newVisibleColumns.length) {
        // any is justified due to untyped nature of useWorkbook
        setCells({} as any);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columns, isSingleFundUser, hasOwnershipsOnSecuritiesWithDividends, isDisabled]);

  const validSecurities = useMemo(() => {
    if (isEmpty(captableInfo) || isEmpty(captableInfo.securities)) {
      return [];
    }
    return (
      captableInfo.securities?.filter(
        security =>
          toString(security.security_type) !== toString(UNISSUED_OPTIONS) && security.convertible_notes.length === 0
      ) ?? []
    );
  }, [captableInfo]);

  const saveConfirmation = useCallback(
    callback => {
      if (isAllocationFinal) {
        showDialog({
          wrapper: ConfirmationDialog,
          wrapperProps: {
            title: 'Confirmation',
          },
          content: ConfirmationSaveFinal,
          actions: [
            {
              label: 'Cancel',
              variant: 'text',
              type: 'primary',
            },
            {
              label: 'Save',
              variant: 'contained',
              type: 'success',
              callback,
            },
          ],
        });
      }
    },
    [isAllocationFinal, showDialog]
  );

  const getUpdatedFOColumn = (foColumn: ReverseParsedFundOwnershipDetail) => {
    if (foColumn.custom_security_name) {
      return {
        ...foColumn,
        security: null,
      };
    }
    return foColumn;
  };

  const saveProforma = useCallback(() => {
    if (isDisabled) {
      setIsDisabled(false);
    }
    const updatedTableData = spreadsheet?.reverseParse();
    saveAsNewProforma({
      ...updatedTableData,
      fund_ownership: {
        ...updatedTableData.fundOwnership,
        fund_ownership_detail: updatedTableData.fundOwnership?.fund_ownership_detail?.map(
          (foColumn: ReverseParsedFundOwnershipDetail) => getUpdatedFOColumn(foColumn)
        ),
      },
      name: formState.values.name ?? `${captableInfo?.name} Proforma`,
    });
  }, [captableInfo, formState.values.name, isDisabled, saveAsNewProforma, spreadsheet]);

  const saveFundOwnership = useCallback(async () => {
    // Save files in widget
    if (!isEmpty(filesToSave) && selectedMeasurementDate && captableInfo) {
      addReferenceForExistingDocuments(
        selectedMeasurementDate.id as number,
        filesToSave,
        captableInfo.fund_ownership?.id as number,
        FUND_OWNERSHIP_REFERENCE_TYPE
      );
    }
    // Save notes in widget
    if (!isEmpty(notes) && notesHasChanged) {
      saveNotes();
    }

    if (!isEmpty(spreadsheetNotes) && spreadsheetNotesHasChanged) {
      saveSpreadsheetNotes();
    }

    if (areCellsValid() && captableInfo) {
      const latestTableData = gridDataRef.current as FundOwnershipTableData;
      // this already includes info about deleted fods
      const fundOwnershipInfo = latestTableData.fundOwnership;
      const tmpUpdatedColumns = (fundOwnershipInfo.fund_ownership_detail ?? [])
        .filter(fod => !(fundOwnershipInfo.deleted_fund_ownership_details ?? []).includes(getNumberValue(fod.id)))
        .map(foColumn => getUpdatedFOColumn(foColumn));

      fundOwnershipInfo.fund_ownership_detail = [...tmpUpdatedColumns];
      // update the fund_ownership_detail.fund field

      fundOwnershipInfo.fund_ownership_detail = fundOwnershipInfo.fund_ownership_detail.map(
        (foColumn: ReverseParsedFundOwnershipDetail) => {
          const updatedFoColumn = { ...foColumn };
          const fund = fundList?.find(fund => `${fund.id}` === `${foColumn.fund_entity}`);
          const entity = (companyInfo?.child_companies as RelatedEntity[])?.find(
            entity => `${entity.company_id}` === `${foColumn.fund_entity}`
          );
          delete updatedFoColumn.fund_entity;
          if (!fund && entity) {
            updatedFoColumn.entity = entity.company_id;
            updatedFoColumn.fund = null;
          }
          if (!entity && fund) {
            updatedFoColumn.fund = fund.id as number;
            updatedFoColumn.entity = null;
          }
          return updatedFoColumn;
        }
      );

      if (isAllocationFinal) {
        saveConfirmation(async () => {
          await updateFundOwnershipData(captableInfo.id as number, fundOwnershipInfo);
        });
      } else {
        /* request to /api/cap-tables/<someId>/fund-ownership */
        await updateFundOwnershipData(captableInfo.id as number, fundOwnershipInfo);
      }
      // Reload allocation breadcrumb info to update the status
      if (allocationInfo?.id) {
        reloadAllocationBreadcrumbInfo(allocationInfo.id, companyExchangeRate);
      }
    }
  }, [
    filesToSave,
    saveSpreadsheetNotes,
    spreadsheetNotes,
    spreadsheetNotesHasChanged,
    notes,
    notesHasChanged,
    areCellsValid,
    addReferenceForExistingDocuments,
    selectedMeasurementDate,
    captableInfo,
    saveNotes,
    isAllocationFinal,
    saveConfirmation,
    updateFundOwnershipData,
    reloadAllocationBreadcrumbInfo,
    allocationInfo,
    companyExchangeRate,
    fundList,
    companyInfo,
  ]);

  const savesAsProformaConfirmation = () => {
    setOpenProformaDialog(true);
  };

  // Register Save when we have cap table data. No cleanup here: clearing on every dep change wipes the
  // button when captableInfo is transiently empty (e.g. store reset on measurement-date / company key change).
  useEffect(() => {
    if (!isActiveTab || isEmpty(captableInfo)) {
      return;
    }

    updatePageActions?.({
      mainAction: {
        id: 'fund-ownership',
        label: 'Save',
        isActive: isDisabled,
        callback: saveFundOwnership,
        isLock: isAllocationFinal,
      },
      secondaryActions: [
        {
          label: 'Save as New Proforma',
          isActive: captableInfo.is_primary,
          callback: savesAsProformaConfirmation,
        },
      ],
    });
    // Intentionally omit saveFundOwnership, updatePageActions, isAllocationFinal, and
    // savesAsProformaConfirmation from deps: they change often or are stable via layout;
    // re-running this effect on every callback identity would spam updatePageActions without
    // fixing the transient empty-captableInfo case this pattern targets.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- see comment above
  }, [captableInfo, isDisabled, filesToSave, notes, isActiveTab]);

  // Clear header actions only when leaving this tab or unmounting.
  useEffect(() => {
    if (!isActiveTab) {
      return;
    }
    return () => {
      updatePageActions?.(null);
    };
  }, [isActiveTab, updatePageActions]);

  useEffect(() => {
    const fetchSelectedUserPermissions = async (userId: number) => {
      dispatch(await firmsAction.getSelectedUserPermissions(userId));
    };
    if (user) {
      fetchSelectedUserPermissions(user.id as number);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Get fund ownership info from the global store
  useEffect(() => {
    if (!isEmpty(captableInfo)) {
      getInitialData();
      // Save in a global scope
      (window as any).appGlobal = { ...(window as any).appGlobal, capTable: captableInfo };
    }

    return () => {
      setColumns(undefined);
      if ((window as any).appGlobal?.capTable) {
        delete (window as any).appGlobal.capTable;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [captableInfo, companyInfo, allocationInfo]);

  useEffect(() => {
    if (isEmpty(captableInfo)) {
      setCells({} as any);
      setColumns([]);
    }

    if (captableInfo?.securities) {
      setCustomConfig(rowConfig(hasOwnershipsOnSecuritiesWithDividends, isDisabled));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [captableInfo, companyInfo, hasOwnershipsOnSecuritiesWithDividends, isDisabled]);

  useEffect(() => {
    if (columns) {
      const tmpVisibleColumns = columns.filter(({ is_deleted }) => !is_deleted);
      setVisibleColumns(tmpVisibleColumns);
      const auxConfig = rowConfig(hasOwnershipsOnSecuritiesWithDividends, isDisabled);
      if (!isEqual(customConfig, auxConfig)) {
        // only if the rowConfig change
        setCustomConfig(auxConfig);
      }
      if (!isEmpty(captableInfo) && !isEmpty(capTableVersionsByCompany)) {
        const allSecurities = Object.values(capTableVersionsByCompany)
          .flatMap(version => version?.map(v => v.securities!) || [])
          .reduce((acc, curr) => [...acc, ...curr], []) as unknown as SecurityModel[];

        const tmpReferencedValues = getReferences({
          columns: tmpVisibleColumns,
          funds: fundList ?? [],
          securities: allSecurities,
        });
        setReferencedValues(tmpReferencedValues);
      }
      if (!tmpVisibleColumns.length) {
        // any is justified due to untyped nature of useWorkbook
        setCells({} as any);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columns, isSingleFundUser, isDisabled, capTableVersionsByCompany, hasOwnershipsOnSecuritiesWithDividends]);

  useEffect(() => {
    if (columns && visibleColumns) {
      setResetColumns(
        isEqual(
          columns.filter(column => !column.is_deleted),
          visibleColumns
        )
      );
    }
  }, [columns, visibleColumns]);

  useEffect(() => {
    if (columns) {
      setFilteredByFundsColumns(columns.filter(({ is_deleted }) => !is_deleted));
    }
  }, [columns]);

  const dbColumns = useMemo(() => captableInfo?.fund_ownership?.fund_ownership_detail, [captableInfo]);
  const hasFundsFilter = useMemo(
    () =>
      !!columns &&
      uniq(columns.map(column => column.fund)).length > 1 &&
      !isDisabled &&
      !!dbColumns &&
      dbColumns.length > 1,
    [columns, dbColumns, isDisabled]
  );
  const hasSecuritiesFilter = useMemo(() => {
    if (!columns || isDisabled || !dbColumns || dbColumns.length <= 1) {
      return false;
    }

    return (
      uniq(
        columns.map(
          column => `${column.security ?? 'custom'}-${column.custom_security_name ?? column.security_name ?? ''}`
        )
      ).length > 1
    );
  }, [columns, dbColumns, isDisabled]);

  const measurementDate = useMemo(
    () => (selectedMeasurementDate ? gridShortDate(selectedMeasurementDate.date) : undefined),
    [selectedMeasurementDate]
  );

  useKeyEventForFabButton(isShowLoadingProgress || isDisabled);

  useHydrateCaptableCellAnnotations(captableInfo);

  const fundOwnershipContext = useMemo<FundOwnershipContextType>(
    () => ({
      isDisabled,
      measurementDate: measurementDate ?? '',
      validSecurities,
      tableData,
      selectedMeasurementDate,
      fundList: fundList ?? [],
      capTableVersionsByCompany,
      fundEntityListValues,
      setFundEntityListValues: handleFundEntityListValues,
      securityListValues,
      setSecurityListValues: handleSecurityListValues,
      spreadsheetTableData,
      fieldAttributes,
    }),
    [
      fundList,
      isDisabled,
      measurementDate,
      selectedMeasurementDate,
      tableData,
      validSecurities,
      fundEntityListValues,
      handleFundEntityListValues,
      securityListValues,
      handleSecurityListValues,
      capTableVersionsByCompany,
      spreadsheetTableData,
      fieldAttributes,
    ]
  );

  const renderProformaCaptable = useMemo(
    () => (
      <ProformaCaptableDialog
        editMode={false}
        initialValues={initialFormValues}
        isValid={formState?.isValid || false}
        onClose={() => setOpenProformaDialog(false)}
        onFormChange={updatedFormState => setFormState(updatedFormState)}
        onSave={saveProforma}
        open={openProformaDialog || false}
        tableTerms={{ tableName: 'Cap Table', columnName: '', pluralColumnName: '' }}
        title="New Proforma Cap Table"
      />
    ),
    [formState, initialFormValues, openProformaDialog, saveProforma]
  );

  const isFundOwnershipEmpty = !isEmpty(captableInfo) && isEmpty(validSecurities) && isEmpty(visibleColumns);
  const isFundOwnershipForbidden = isEqual(allocationInfo, ERROR_403);
  let fundOwnershipExportStatus: 'loading' | 'missing' | 'ready' = 'ready';
  if (
    !captableId ||
    !isLoaded ||
    isShowLoadingProgress ||
    (isExportMode && (!spreadsheet || !workbook?.fundOwnership))
  ) {
    fundOwnershipExportStatus = 'loading';
  } else if (isFundOwnershipForbidden || isFundOwnershipEmpty) {
    fundOwnershipExportStatus = 'missing';
  }
  useExportSignal({
    target: PdfExportTargets.FUND_OWNERSHIP.id,
    status: fundOwnershipExportStatus,
    enabled: isExportMode,
  });

  if (!captableId || !isLoaded) {
    return <GridSkeleton />;
  }

  if (isEqual(allocationInfo, ERROR_403)) {
    return (
      <MessageBox
        title={FOC.FORBIDDEN_FUNDOWNERSHIP_INFO}
        tagline={FOC.GET_FUNDOWNERSHIP_INFO_ACCESS}
        fullWidth={false}
      />
    );
  }

  if (!isEmpty(captableInfo) && isEmpty(validSecurities) && isEmpty(visibleColumns)) {
    return (
      <EmptyCapTableMessage
        title=""
        tagline=""
        secondaryAction={
          <div>
            <Button
              className={classes.emptyTableSecondaryAction}
              variant="text"
              color="primary"
              onClick={addCustomSecurityOwnership}>
              {FOC.ADD_CUSTOM_SECURITY_OWNERSHIP}
            </Button>
          </div>
        }
      />
    );
  }

  return (
    <StyledFundOwnershipContextProvider value={fundOwnershipContext}>
      <CellAnnotationProvider>
        {hasFundsFilter && (
          <FundsChipContainer
            columns={columns as ReverseParsedFundOwnershipDetail[]}
            setActiveFundsColumns={hasSecuritiesFilter ? setFilteredByFundsColumns : setVisibleColumns}
            reset={resetColumns}
          />
        )}
        {hasSecuritiesFilter && (
          <SecuritiesChipContainer
            columns={filteredByFundsColumns}
            setActiveSecuritiesColumns={setVisibleColumns}
            reset={resetColumns}
          />
        )}
        {/* Proforma Cap Table */}
        {renderProformaCaptable}
        {isDisabled && isAllocationFinal && !isExportMode && (
          <AllocationFinalMessage
            tableTerms={tableTerms}
            finalAllocations={finalAllocations}
            setIsDisabled={setIsDisabled}
            savesAsProformaConfirmation={savesAsProformaConfirmation}
            variant
            actions={[
              {
                label: 'Duplicate',
                buttonProps: {
                  variant: 'contained',
                  size: 'small',
                  disabled: viewOnlyUser,
                },
                callback: savesAsProformaConfirmation,
              },
            ]}
            viewOnlyUser={viewOnlyUser}
          />
        )}
        {isAllocationFinal && !isDisabled && !isExportMode && (
          <Alert isAlertVisible>{FUND_OWNERSHIP_LOCKED_MESSAGE}</Alert>
        )}
        <WorkbookContext.Provider value={workbookContextValue}>
          <ScalarSpreadsheet
            {...spreadsheet}
            sheet={spreadsheet}
            onChange={onChange}
            data={spreadsheet ? spreadsheet.data : []}
            className={classes.spreadsheet}
            cloneColumn={cloneColumn}
            dbColumns={dbColumns}
            updateCells={setCells}
            customDeleteConfirmation={false}
            referencedValues={referencedValues}
            reverseParserOutputAccessor={fundOwnershipDetailsTraverser}
            workbook={workbook}
            deleteColumn={deleteColumn}
            format={format}
            formatDispatch={formatDispatch}
            setColumns={setVisibleColumns}
            setIsDisplayingRowNumbers={setIsDisplayingRowNumbers}>
            {isEmpty(visibleColumns) ? ((<EmptyTableMessage tableTerms={tableTerms} />) as any) : null}
          </ScalarSpreadsheet>
        </WorkbookContext.Provider>
        {captableInfo?.is_primary && !isExportMode && (
          <WidgetDrawer
            applicationContext={WidgetDrawerApplicationContext.FUND_OWNERSHIP}
            documentReferencesProps={{
              referenceType: FUND_OWNERSHIP_REFERENCE_TYPE,
              selectedMeasurementDate,
              currentPage: FUND_OWNERSHIP_REFERENCE_TITLE,
              referencedFeatureId: captableInfo?.fund_ownership?.id,
              filesToSave,
              setFilesToSave,
              isDisplayingRowNumbers,
              isDisabled,
            }}
            notesProps={{
              pageType: FUND_OWNERSHIP_REFERENCE_TYPE,
              pageTypeKey: FUND_OWNERSHIP_PAGE_KEY,
              pageTypeId: captableInfo?.fund_ownership?.id,
              notes,
              setNotes,
              onAddNote,
              onUpdateNotes,
              onDeleteNote,
              isDisabled,
            }}
            spreadsheetNotesProps={{
              pageType: FUND_OWNERSHIP_REFERENCE_TYPE,
              pageTypeKey: FUND_OWNERSHIP_PAGE_KEY,
              pageTypeId: captableInfo?.fund_ownership?.id,
              notes: spreadsheetNotes,
              setNotes: setSpreadsheetNotes,
              saveNotes: () => {
                saveSpreadsheetNotes();
                return undefined;
              },
              handleOnDeleteNote: handleOnDeleteSpreadsheetNote,
            }}
          />
        )}
        {!isExportMode && !isEmpty(captableInfo) && isEmpty(validSecurities) && !isEmpty(visibleColumns) ? (
          <ExtendedFabButton
            addBottomMarginForDrawer
            id="fund-ownership-add-btn"
            disabled={isShowLoadingProgress || isDisabled}
            label={FOC.ADD_CUSTOM_SECURITY_OWNERSHIP}
            onClick={addCustomSecurityOwnership}
          />
        ) : (
          <FloatingButtonGroup
            style={{
              marginBottom: `var(${APP_DRAGGABLE_DRAWER_HEIGHT_VAR}, ${MIN_DRAWER_HEIGHT}px)`,
              zIndex: Z_INDEX_FOR_ADD_BUTTONS,
            }}
            id="fund-ownership-fab-group"
            disabled={isShowLoadingProgress || isDisabled}
            mainAction={{
              key: 'fund-ownership-add-btn',
              label: FOC.ADD_FUND_OWNERSHIP,
              onClick: () => addColumn(),
              icon: AddIcon,
            }}
            secondaryActions={[
              {
                key: 1,
                label: FOC.ADD_CUSTOM_SECURITY_OWNERSHIP,
                onClick: addCustomSecurityOwnership,
                icon: AddIcon,
              },
            ]}
          />
        )}
      </CellAnnotationProvider>
    </StyledFundOwnershipContextProvider>
  );
};

export default FundOwnership;
